/**
 * KeywordsInputStep - Step 2 (Path A) or Step 4 (Path B): Enter/review keywords
 *
 * Uses KeywordInputForm which bundles:
 * - Keyword textarea with dedup warning
 * - ResearchedKeywordsPanel (sortable table: KD / volume / opportunity)
 * - TopicSuggestionsPanel (AI topic suggest)
 * - Blog title input with AI "Suggest" button
 *
 * "View all research sessions" opens ResearchSessionsDrawer — the full
 * slide-over with seed keyword input, AI suggest, live progress tracking,
 * and all completed sessions with sortable cluster tables.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { KeywordInputForm } from '../KeywordInputForm';
import { ResearchSessionsDrawer } from '../ResearchSessionsDrawer';
import { useTopicalMaps, useTitleSuggestions } from '@/hooks/useContentPlannerAPI';
import { useBrandBible } from '@/hooks/useBrandBible';
import { useTenantStore } from '@/stores/useTenantStore';
import { toast } from 'sonner';

// ── Helpers ─────────────────────────────────────────────────────────────────

const parseKeywords = (text: string): string[] =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && line.length <= 255);

const keywordsToText = (keywords: string[]): string => keywords.join('\n');

// ── Progress messages during title suggestion ────────────────────────────────

const TITLE_SUGGEST_MESSAGES = [
  'Analyzing competitor titles from search results...',
  'Researching top-ranking pages for this keyword...',
  'Generating SEO-optimized title options with AI...',
  'Evaluating title patterns and click-through potential...',
];

// ── Props ────────────────────────────────────────────────────────────────────

interface KeywordsInputStepProps {
  keywords: string[];
  onUpdateKeywords: (keywords: string[]) => void;
  blogTitle: string;
  onUpdateBlogTitle: (title: string) => void;
  selectedTopic: string;
  onUpdateTopic: (topic: string) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

const KeywordsInputStep: React.FC<KeywordsInputStepProps> = ({
  keywords,
  onUpdateKeywords,
  blogTitle,
  onUpdateBlogTitle,
  selectedTopic,
  onUpdateTopic,
}) => {
  const { currentTenant } = useTenantStore();
  const { data: brandBible } = useBrandBible(currentTenant);

  // Keyword textarea text state (derived from keywords prop on mount)
  const [keywordsText, setKeywordsText] = useState(() => keywordsToText(keywords));
  const [keywordsError, setKeywordsError] = useState('');

  // Research panel state (inline accordion in KeywordInputForm)
  const [expandedMapId, setExpandedMapId] = useState<string | null>(null);

  // Research sessions drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Title suggestion state
  const [titleSuggestions, setTitleSuggestions] = useState<string[]>([]);
  const [isSuggestingTitles, setIsSuggestingTitles] = useState(false);
  const [titleSuggestMsgIndex, setTitleSuggestMsgIndex] = useState(0);
  const [competitorCount, setCompetitorCount] = useState(0);
  // Track the keyword+topic we last triggered a fetch for
  const [titleFetchKey, setTitleFetchKey] = useState<{ keyword: string; topic: string } | null>(null);

  // All topical maps (used by both inline panel and drawer)
  const {
    data: mapsData,
    isLoading: mapsLoading,
    isPending: mapsPending,
    refetch: refetchMaps,
  } = useTopicalMaps(currentTenant);

  const allMaps = useMemo(() => mapsData?.maps || [], [mapsData]);
  const completedMaps = useMemo(
    () => allMaps.filter((m) => m.status === 'completed'),
    [allMaps],
  );

  // Cached title suggestions (1-hour staleTime via React Query)
  const { data: cachedTitles, isFetching: isFetchingTitles } = useTitleSuggestions(
    titleFetchKey?.keyword ?? null,
    titleFetchKey?.topic,
    !!titleFetchKey,
  );

  // Drive the loading message rotation while fetching
  useEffect(() => {
    if (!isFetchingTitles) return;
    const interval = setInterval(() => {
      setTitleSuggestMsgIndex((i) => (i + 1) % TITLE_SUGGEST_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isFetchingTitles]);

  // Sync cached result into local state when it arrives
  useEffect(() => {
    if (cachedTitles && !isFetchingTitles) {
      setTitleSuggestions(cachedTitles.suggestions || []);
      setCompetitorCount((cachedTitles as any).competitor_count || 0);
      setIsSuggestingTitles(false);
    }
  }, [cachedTitles, isFetchingTitles]);

  // Current keywords as Set for dedup checking
  const currentKeywordsSet = useMemo(
    () => new Set(parseKeywords(keywordsText).map((k) => k.toLowerCase())),
    [keywordsText],
  );

  // ── Keyword textarea handlers ──────────────────────────────────────────────

  const handleKeywordsChange = useCallback(
    (text: string) => {
      setKeywordsText(text);
      const parsed = parseKeywords(text);
      if (parsed.length > 50) {
        setKeywordsError('Maximum 50 keywords allowed at once');
      } else {
        setKeywordsError('');
        onUpdateKeywords(parsed);
      }
      // Clear stale title suggestions when the primary keyword changes
      const newPrimary = parsed[0]?.toLowerCase() ?? '';
      const fetchedPrimary = titleFetchKey?.keyword.toLowerCase() ?? '';
      if (newPrimary !== fetchedPrimary) {
        setTitleSuggestions([]);
        setTitleFetchKey(null);
      }
    },
    [onUpdateKeywords, titleFetchKey],
  );

  const addKeywordToTextarea = useCallback(
    (keyword: string) => {
      const trimmed = keyword.trim();
      if (!trimmed) return;
      if (!currentKeywordsSet.has(trimmed.toLowerCase())) {
        const updated = keywordsText.trim()
          ? `${keywordsText}\n${trimmed}`
          : trimmed;
        handleKeywordsChange(updated);
      }
    },
    [keywordsText, currentKeywordsSet, handleKeywordsChange],
  );

  const addKeywordsToTextarea = useCallback(
    (newKeywords: string[]) => {
      const toAdd = newKeywords.filter(
        (k) => k.trim() && !currentKeywordsSet.has(k.trim().toLowerCase()),
      );
      if (toAdd.length > 0) {
        const updated = keywordsText.trim()
          ? `${keywordsText}\n${toAdd.join('\n')}`
          : toAdd.join('\n');
        handleKeywordsChange(updated);
      }
    },
    [keywordsText, currentKeywordsSet, handleKeywordsChange],
  );

  // ── Title suggestion ───────────────────────────────────────────────────────

  const handleSuggestTitles = useCallback(() => {
    const parsed = parseKeywords(keywordsText);
    const primaryKeyword = parsed[0];
    if (!primaryKeyword) return;

    const key = { keyword: primaryKeyword, topic: selectedTopic || '' };

    // If same keyword+topic already fetched, reuse cached result immediately
    if (
      titleFetchKey?.keyword === key.keyword &&
      titleFetchKey?.topic === key.topic &&
      cachedTitles
    ) {
      setTitleSuggestions(cachedTitles.suggestions || []);
      return;
    }

    // Trigger new fetch
    setIsSuggestingTitles(true);
    setTitleSuggestMsgIndex(0);
    setTitleFetchKey(key);
  }, [keywordsText, selectedTopic, titleFetchKey, cachedTitles]);

  const handleSelectTitleSuggestion = useCallback(
    (suggestion: string) => {
      onUpdateBlogTitle(suggestion);
      setTitleSuggestions([]);
    },
    [onUpdateBlogTitle],
  );

  // ── Topic select ───────────────────────────────────────────────────────────

  const handleTopicSelect = useCallback(
    (topic: string) => {
      onUpdateTopic(topic);
    },
    [onUpdateTopic],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <KeywordInputForm
        keywordsText={keywordsText}
        onKeywordsChange={handleKeywordsChange}
        keywordCount={parseKeywords(keywordsText).length}
        keywordsError={keywordsError}
        tenantId={currentTenant}
        brandBible={brandBible ?? null}
        completedMaps={completedMaps}
        mapsLoading={mapsLoading}
        mapsPending={mapsPending ?? false}
        expandedMapId={expandedMapId}
        onToggleMap={(mapId) =>
          setExpandedMapId((prev) => (prev === mapId ? null : mapId))
        }
        onShowMoreResearch={() => setDrawerOpen(true)}
        totalMapsCount={allMaps.length}
        addKeywordToTextarea={addKeywordToTextarea}
        addKeywordsToTextarea={addKeywordsToTextarea}
        currentKeywordsSet={currentKeywordsSet}
        parseKeywords={parseKeywords}
        blogTitle={blogTitle}
        onBlogTitleChange={onUpdateBlogTitle}
        selectedTopic={selectedTopic}
        onTopicSelect={handleTopicSelect}
        isSuggestingTitles={isSuggestingTitles}
        titleSuggestions={titleSuggestions}
        competitorCount={competitorCount}
        titleSuggestMessage={TITLE_SUGGEST_MESSAGES[titleSuggestMsgIndex]}
        onSuggestTitles={handleSuggestTitles}
        onSelectTitleSuggestion={handleSelectTitleSuggestion}
        isSubmitting={false}
      />

      {/* Full research sessions drawer — opens when "View all X sessions" is clicked */}
      {currentTenant && (
        <ResearchSessionsDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          allMaps={allMaps}
          tenantId={currentTenant}
          onAddKeyword={addKeywordToTextarea}
          onAddKeywords={addKeywordsToTextarea}
          currentKeywordsSet={currentKeywordsSet}
          refetchMaps={refetchMaps}
          brandBible={brandBible ?? null}
        />
      )}
    </>
  );
};

export default KeywordsInputStep;
